/**
 * Validation Helpers
 * Helper functions untuk validation logic
 */

import type { ProductChangeData, WarningType } from './index';

// ============================================================================
// Form Validation Helpers
// ============================================================================

/**
 * Validate required field
 */
export const isRequired = (value: any): boolean => {
  if (typeof value === 'string') {
    return value.trim().length > 0;
  }
  return value !== null && value !== undefined;
};

/**
 * Validate phone number format (Indonesia)
 */
export const validatePhoneNumber = (phone: string): boolean => {
  if (!phone) return false;

  const cleaned = phone.replace(/[^\d+]/g, '');
  const phoneRegex = /^\+62\d{9,13}$/;

  return phoneRegex.test(cleaned);
};

/**
 * Validate email format
 */
export const validateEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

// ============================================================================
// File Validation Helpers
// ============================================================================

export const ALLOWED_FILE_TYPES = ['pdf', 'png', 'jpg', 'jpeg'];
export const MAX_FILE_SIZE_MB = 2;

/**
 * Validate file type (PDF, PNG, JPG only)
 */
export const validateFileType = (file: File): boolean => {
  const extension = file.name.split('.').pop()?.toLowerCase() || '';
  return ALLOWED_FILE_TYPES.includes(extension);
};

/**
 * Validate file size (max 2MB)
 */
export const validateFileSize = (file: File): boolean => {
  const maxSizeInBytes = MAX_FILE_SIZE_MB * 1024 * 1024;
  return file.size <= maxSizeInBytes;
};

/**
 * Validate file - combine type and size check
 */
export const validateFile = (
  file: File
): { isValid: boolean; error?: string } => {
  if (!validateFileType(file)) {
    return {
      isValid: false,
      error: 'Format file tidak didukung. Gunakan PDF, PNG, atau JPG.',
    };
  }

  if (!validateFileSize(file)) {
    return {
      isValid: false,
      error: `Ukuran file melebihi ${MAX_FILE_SIZE_MB}MB.`,
    };
  }

  return { isValid: true };
};

/**
 * Validate multiple files
 */
export const validateFiles = (
  files: File[]
): { isValid: boolean; errors: string[] } => {
  const errors: string[] = [];

  files.forEach((file, index) => {
    const validation = validateFile(file);
    if (!validation.isValid && validation.error) {
      errors.push(`File ${index + 1}: ${validation.error}`);
    }
  });

  return {
    isValid: errors.length === 0,
    errors,
  };
};

// ============================================================================
// Order Validation Helpers
// ============================================================================

/**
 * Check if product has stock changes
 */
export const hasStockChanges = (
  productChanges: ProductChangeData[]
): boolean => {
  return productChanges.some((product) => product.is_stock_diff);
};

/**
 * Check if product has price changes
 */
export const hasPriceChanges = (
  productChanges: ProductChangeData[]
): boolean => {
  return productChanges.some((product) => product.is_price_diff);
};

/**
 * Check if product is out of stock
 */
export const isOutOfStock = (product: ProductChangeData): boolean => {
  return product.is_stock_diff && product.stock_diff === 0;
};

/**
 * Get out of stock products
 */
export const getOutOfStockProducts = (
  productChanges: ProductChangeData[]
): ProductChangeData[] => {
  return productChanges.filter((product) => isOutOfStock(product));
};

/**
 * Check if all products are out of stock
 */
export const areAllProductsOutOfStock = (
  productChanges: ProductChangeData[]
): boolean => {
  return productChanges.every((product) => isOutOfStock(product));
};

// ============================================================================
// Warning Type Helpers
// ============================================================================

/**
 * Get modal title based on warning type
 */
export const getWarningModalTitle = (warningType: WarningType): string => {
  const titles: Record<string, string> = {
    price_stock_changes:
      'Terdapat perubahan harga atau kuantitas pada pesanan Anda',
    no_product: 'PO Anda belum berhasil dibuat',
    min_order: 'PO Anda belum berhasil dibuat',
    min_order_total_diff: 'PO Anda belum berhasil dibuat',
    promo_invalid: 'Promo Sudah Tidak Berlaku',
    voucher_invalid: 'Voucher Tidak Dapat Digunakan',
  };

  return titles[warningType || ''] || 'Notifikasi';
};

/**
 * Get modal size based on warning type
 */
export const getWarningModalSize = (
  warningType: WarningType
): 'xl' | '2xl' | '3xl' => {
  if (
    warningType === 'price_stock_changes' ||
    warningType === 'no_product' ||
    warningType === 'promo_invalid' ||
    warningType === 'voucher_invalid'
  ) {
    return '3xl';
  }
  return 'xl';
};

/**
 * Check if warning type allows continue
 */
export const canContinueOrder = (warningType: WarningType): boolean => {
  return (
    warningType === 'price_stock_changes' ||
    warningType === 'promo_invalid' ||
    warningType === 'voucher_invalid'
  );
};

/**
 * Get footer warning message based on warning type
 */
export const getFooterWarningMessage = (warningType: WarningType): string => {
  const messages: Record<string, string> = {
    no_product:
      'Seluruh produk yang Anda pesan stoknya tidak tersedia. Silakan kembali ke halaman keranjang untuk melihat rekomendasi produk pengganti.',
    min_order:
      'Total pesanan Anda belum mencapai minimum order. Silakan tambahkan produk lain atau kembali ke halaman keranjang.',
    min_order_total_diff:
      'Total pesanan Anda belum mencapai minimum order. Silakan tambahkan produk lain atau kembali ke halaman keranjang.',
  };
  return (
    messages[warningType || ''] ||
    'Silakan lanjutkan pesanan atau kembali ke halaman keranjang untuk melihat perubahan.'
  );
};

/**
 * Get return button variant based on warning type
 */
export const getReturnButtonVariant = (
  warningType: WarningType
): 'solid' | 'outline' => {
  const outlineTypes = [
    'price_stock_changes',
    'no_product',
    'promo_invalid',
    'voucher_invalid',
  ];
  return outlineTypes.includes(warningType || '') ? 'outline' : 'solid';
};

/**
 * Get continue button text based on warning type
 */
export const getContinueButtonText = (warningType: WarningType): string => {
  const texts: Record<string, string> = {
    price_stock_changes: 'Lanjutkan Pesanan',
    voucher_invalid: 'Lanjutkan Tanpa Voucher',
  };
  return texts[warningType || ''] || 'Ya, Lanjutkan';
};

// ============================================================================
// Promo Validation Helpers
// ============================================================================

/**
 * Check if promo is expired
 */
export const isPromoExpired = (endDate: string): boolean => {
  const now = new Date();
  const end = new Date(endDate);
  return now > end;
};

/**
 * Check if promo is not started yet
 */
export const isPromoNotStarted = (startDate: string): boolean => {
  const now = new Date();
  const start = new Date(startDate);
  return now < start;
};

/**
 * Get promo status message
 */
export const getPromoStatusMessage = (
  startDate: string,
  endDate: string
): string => {
  if (isPromoNotStarted(startDate)) {
    return 'Promo belum dimulai';
  }
  if (isPromoExpired(endDate)) {
    return 'Promo sudah berakhir';
  }
  return 'Promo aktif';
};

// ============================================================================
// Error Message Helpers
// ============================================================================

/**
 * Get user-friendly error message
 */
export const getUserFriendlyErrorMessage = (errorCode: string): string => {
  const errorMessages: Record<string, string> = {
    ALL_ITEM_CANT_BE_PROCESSED:
      'Mohon maaf, stok produk yang Anda pilih saat ini sedang kosong. Silakan cari produk serupa.',
    MIN_ORDER_NOT_REACHED:
      'Minimum order belum tercapai. Silakan tambah kuantitas atau produk lainnya.',
    MIN_ORDER_NOT_REACHED_DUE_TO_TOTAL_DIFF:
      'Ada barang yang mengalami perubahan harga atau stock sehingga tidak mencapai minimum order. Silakan tambah kuantitas atau produk lainnya agar mencapai minimum order.',
    DIFF_PRICE_OR_STOCK:
      'Terdapat perubahan harga atau kuantitas pada pesanan Anda',
    PARTIALLY_OUT_OF_STOCK: 'Beberapa produk mengalami perubahan stok',
    PROMO_INVALID:
      'Promo berikut tidak bisa diproses karena telah kedaluarsa atau belum memenuhi syarat dan ketentuan yang berlaku.',
  };

  return (
    errorMessages[errorCode] ||
    'Terjadi kesalahan. Silakan coba lagi atau hubungi customer service.'
  );
};

/**
 * Determine warning type from error code
 */
export const getWarningTypeFromErrorCode = (errorCode: string): WarningType => {
  const mapping: Record<string, WarningType> = {
    ALL_ITEM_CANT_BE_PROCESSED: 'no_product',
    MIN_ORDER_NOT_REACHED: 'min_order',
    MIN_ORDER_NOT_REACHED_DUE_TO_TOTAL_DIFF: 'min_order_total_diff',
    DIFF_PRICE_OR_STOCK: 'price_stock_changes',
    PARTIALLY_OUT_OF_STOCK: 'price_stock_changes',
  };

  return mapping[errorCode] || null;
};

// ============================================================================
// Misc Validation Helpers
// ============================================================================

/**
 * Check if order is already submitted
 */
export const isOrderSubmitted = (isSubmitted?: boolean): boolean => {
  return isSubmitted === true;
};

/**
 * Check if minimum order value is met
 */
export const meetsMinimumOrder = (
  total: number,
  minimumOrder: number
): boolean => {
  return total >= minimumOrder;
};

/**
 * Sanitize input string (remove script tags, etc)
 */
export const sanitizeInput = (input: string): string => {
  return input
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
    .replace(/<[^>]*>/g, '')
    .trim();
};
